import React, { createContext, useEffect, useReducer } from "react";
import { jwtDecode } from "jwt-decode";
import { LOGIN, LOGOUT } from "contexts/auth-reducer/actions";
import authReducer from "contexts/auth-reducer/auth";
import Loader from "components/Loader";
import { KeyedObject } from "types/root";
import { AuthProps, JWTContextType, TokenResponse, UserData } from "types/auth";
import api from "utils/api";
import { ApiBase } from "config";
import { showError } from "utils/showMessages";

const initialState: AuthProps = {
  isLoggedIn: false,
  isInitialized: false,
  user: null,
  modules: null,
};

const verifyToken: (st: string) => boolean = (serviceToken) => {
  if (!serviceToken) {
    return false;
  }
  const decoded: KeyedObject = jwtDecode(serviceToken);
  return decoded.exp > Date.now() / 1000;
};

const setSession = (serviceToken?: string | null) => {
  if (serviceToken) {
    localStorage.setItem("serviceToken", serviceToken);
  } else {
    localStorage.removeItem("serviceToken");
  }
};

const JWTContext = createContext<JWTContextType | null>(null);

export const JWTProvider = ({ children }: { children: React.ReactElement }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  const init = async () => {
    try {
      const serviceToken = window.localStorage.getItem("serviceToken");
      if (serviceToken && verifyToken(serviceToken)) {
        setSession(serviceToken);
        const result = await api.get<UserData>(
          `${ApiBase}/api/account/manage/info`
        );
        if (result.status && result.apiData) {
          const user = { ...result.apiData?.user };
          const modules = { ...result.apiData?.modules };
          dispatch({
            type: LOGIN,
            payload: {
              isLoggedIn: true,
              user,
              modules,
            },
          });
        }
      } else {
        dispatch({
          type: LOGOUT,
        });
      }
    } catch (err) {
      console.error(err);
      dispatch({
        type: LOGOUT,
      });
    }
  };

  useEffect(() => {
    init();
  }, []);

  const login = async (email: string, password: string, clientCode: string) => {
    const result = await api.tempFetch<TokenResponse>(
      `${ApiBase}/api/account/login`,
      {
        method: "POST",
        body: JSON.stringify({ email, password, clientCode }),
      }
    );

    if (result.status == 200 && result.apiData) {
      const { accessToken } = result.apiData || {};
      setSession(accessToken);
      sessionStorage.setItem("pending_invoices_alert_shown", "false");
      await init();
    } else {
      const errors: string[] = [];
      for (const [key] of Object.entries(result.errors || [])) {
        errors.push(key);
      }
      const error = errors.join(". ").trim();
      showError(error);
    }
  };

  const switchLogin = async (libraryID: string | number) => {
    const result = await api.post<TokenResponse>(
      `${ApiBase}/api/account/switchLogin`,
      { libraryID: libraryID }
    );
    if (result.status == 200 && result.apiData) {
      const { accessToken } = result.apiData || {};
      setSession(accessToken);
      await init();
    } else {
      const errors: string[] = [];
      for (const [key] of Object.entries(result.errors || [])) {
        errors.push(key);
      }
      const error = errors.join(". ").trim();
      showError(error);
    }
  };

  const logout = () => {
    setSession(null);
    dispatch({ type: LOGOUT });
  };

  const resetPassword = async (email: string) => {
    console.log("email - ", email);
  };

  const updateProfile = () => {};

  if (state.isInitialized !== undefined && !state.isInitialized) {
    return <Loader />;
  }

  return (
    <JWTContext.Provider
      value={{
        ...state,
        login,
        logout,
        resetPassword,
        updateProfile,
        switchLogin,
      }}
    >
      {children}
    </JWTContext.Provider>
  );
};

export default JWTContext;
